import { z } from "zod";
import * as turf from "@turf/turf";
import {
	PlantMethod,
	OrganicStatus,
	BoundaryType,
} from "seedgreen-shared/dtos/PlantingDTO";
import dayjs from "dayjs";

// Shared validation definitions
// Preprocess to handle empty strings from number inputs (convert to undefined)
const acresValidation = z.preprocess(
	(val) => (val === "" || val === null ? undefined : val),
	z.number().gt(0, "Acres must be greater than 0").optional(),
);
const acresActualValidation = z.preprocess(
	(val) => (val === "" || val === null ? undefined : val),
	z.number().gt(0, "Actual acres must be greater than 0").optional(),
);
const sublotValidation = z.string().optional();
const bedCountValidation = z
	.number()
	.max(999999, "Bed count must be less than 1,000,000")
	.optional();
const bedSizeValidation = z
	.number()
	.max(9999, "Bed size must be less than 10,000 inches")
	.optional();
const bedLinesValidation = z
	.number()
	.max(999999, "Bed lines must be less than 1,000,000")
	.optional();
const plantSpacingValidation = z
	.number()
	.max(999, "Plant spacing must be less than 1,000 inches")
	.optional();

/**
 * Zod schema for planting validation that matches the logic from SavePlantingDTOValidator in PlantingValidators.cs
 */
export const plantingSchema = z
	.object({
		// CustomerId - Optional on frontend and managed by the backend
		customerId: z.number().optional(),

		// Dates - at least one wet date required
		wetDate: z.string().optional(),
		wetDateActual: z.string().optional(),
		plantDate: z.string().optional(),
		plantDateActual: z.string().optional(),
		transplantDate: z.string().optional(),
		transplantDateActual: z.string().optional(),
		endDate: z.string().optional(),
		endDateActual: z.string().optional(),

		// Naming - either lotIds or name required
		lotIds: z.array(z.number()).default([]),
		name: z.string().optional(),

		// Crop - either cropId or cropIdActual required
		cropId: z.number().optional(),
		cropIdActual: z.number().optional(),

		// Location
		ranchId: z.number().optional(),
		sublot: sublotValidation,
		sublotActual: sublotValidation,
		sublotCurrent: sublotValidation,

		// Basic info
		externalId: z.string().optional(),
		isVisible: z.boolean().default(true),
		organizationId: z.number().optional(),
		growerId: z.number().optional(),
		nurseryId: z.number().optional(),
		nurseryIdActual: z.number().optional(),

		// Acreage - must be greater than 0 if provided
		acres: acresValidation,
		acresActual: acresActualValidation,
		acresCurrent: acresValidation,

		// Enums
		plantMethod: z
			.nativeEnum(PlantMethod, {
				message: "Invalid plant method",
			})
			.optional(),
		organicStatus: z
			.nativeEnum(OrganicStatus, {
				message: "Invalid organic status",
			})
			.optional(),

		// IDs
		harvestPurposeTypeId: z.number().optional(),
		organicAuthorityId: z.number().optional(),
		organicAuthorityLabelId: z.number().optional(),
		rotation: z.number().optional(),
		rotationActual: z.number().optional(),

		// Bed configuration with reasonable limits
		bedCount: bedCountValidation,
		bedCountActual: bedCountValidation,
		bedCountCurrent: bedCountValidation,
		bedSizeInches: bedSizeValidation,
		bedSizeInchesActual: bedSizeValidation,
		bedSizeInchesCurrent: bedSizeValidation,
		bedLines: bedLinesValidation,
		bedLinesActual: bedLinesValidation,
		bedLinesCurrent: bedLinesValidation,
		plantSpacingInches: plantSpacingValidation,
		plantSpacingInchesActual: plantSpacingValidation,
		plantSpacingInchesCurrent: plantSpacingValidation,
		bedDirection: z.number().optional(),

		// Boundary fields
		latestBoundary: z
			.any()
			.optional()
			.refine(
				(data) => {
					if (!data) return true; // Allow undefined/null
					try {
						const geometryType = turf.getType(data);
						return (
							geometryType === "Polygon" ||
							geometryType === "MultiPolygon"
						);
					} catch {
						return false;
					}
				},
				{
					message: "Must be a valid GeoJSON Polygon or MultiPolygon",
				},
			),
		latestBoundaryDate: z.string().optional(),
		latestBoundaryType: z
			.nativeEnum(BoundaryType, {
				message: "Invalid boundary type",
			})
			.optional(),

		// Harvest dates
		harvestDates: z.array(z.string()).default([]),
		harvestDatesActual: z.array(z.string()).default([]),

		// Custom data
		customData: z.string().optional(),
	})
	.refine((data) => data.wetDateActual || data.wetDate, {
		message: "An estimated or actual wet date must be provided",
		path: ["wetDate"],
	})
	.refine(
		(data) =>
			data.lotIds.length > 0 ||
			(data.name && data.name.trim().length > 0),
		{
			message: "Either lot IDs or a name is required",
			path: ["lotIds"],
		},
	)
	.refine((data) => data.cropIdActual || data.cropId, {
		message: "Crop must be provided",
		path: ["cropId"],
	})
	.refine((data) => data.acresActual || data.acres, {
		message: "Acres must be provided and must be greater than 0",
		path: ["acres"],
	})
	.refine(
		(data) => {
			// If boundary is provided, boundary type must be provided
			if (data.latestBoundary && !data.latestBoundaryType) {
				return false;
			}
			return true;
		},
		{
			message:
				"A boundary type must be provided if a boundary is provided",
			path: ["latestBoundaryType"],
		},
	)
	.refine(
		(data) => {
			// If boundary is provided, boundary date must be provided
			if (data.latestBoundary && !data.latestBoundaryDate) {
				return false;
			}
			return true;
		},
		{
			message:
				"A boundary date must be provided if a boundary is provided",
			path: ["latestBoundaryDate"],
		},
	)
	.refine(
		(data) => {
			// If any harvest date is provided, none may be before the wet date
			const wetDate = data.wetDateActual || data.wetDate;
			const allHarvestDates = [
				...(data.harvestDatesActual ?? []),
				...(data.harvestDates ?? []),
			].filter(Boolean);

			if (wetDate && allHarvestDates.length > 0) {
				return allHarvestDates.every(
					(date) => !dayjs(date).isBefore(dayjs(wetDate)),
				);
			}
			return true;
		},
		{
			message: "Harvest date cannot be before wet date",
			path: ["harvestDates"],
		},
	);

export type PlantingFormData = z.infer<typeof plantingSchema>;
